import { MatchRepository, ParticipantRepository, TournamentRepository } from '../repositories';
import { logger } from '@/src/core/utils';

export class GenerateKingOfTheHillBracketUseCase {
  constructor(
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<void> {
    logger.debug('KingOfTheHillBracket', 'Starting bracket generation', { tournamentId });

    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    if (tournament.status !== 'setup') {
      throw new Error('Tournament has already started');
    }

    const participants = await this.participantRepository.getByTournamentId(tournamentId);

    if (participants.length < 2) {
      throw new Error('Need at least 2 participants to generate bracket');
    }

    // Sort by seed ascending: lowest seed = initial king
    const sorted = [...participants].sort((a, b) => (a.seed ?? Infinity) - (b.seed ?? Infinity));

    logger.debug('KingOfTheHillBracket', 'Participants sorted', { count: sorted.length });

    // Pass 1: create N-1 matches and collect their IDs
    const savedIds: string[] = [];

    for (let k = 0; k < sorted.length - 1; k++) {
      const isFirst = k === 0;
      const saved = await this.matchRepository.create({
        tournamentId,
        round: k + 1,
        matchNumber: k + 1,
        bracketType: 'winners',
        participant1Id: isFirst ? sorted[0].id : undefined,
        participant2Id: sorted[k + 1].id,
        status: 'pending',
        winnerId: undefined,
        scoreParticipant1: undefined,
        scoreParticipant2: undefined,
        nextMatchId: undefined,
        loserNextMatchId: undefined,
      });
      savedIds.push(saved.id);
    }

    logger.debug('KingOfTheHillBracket', 'Matches created', { count: savedIds.length });

    // Pass 2: link each match to the next via nextMatchId
    for (let k = 0; k < savedIds.length - 1; k++) {
      await this.matchRepository.update(savedIds[k], {
        nextMatchId: savedIds[k + 1],
      });
    }

    await this.tournamentRepository.update(tournamentId, { status: 'in_progress' });

    logger.debug('KingOfTheHillBracket', 'Bracket generated successfully');
  }
}
